import { Skeleton } from "@mui/material";
import { CaseType } from "../cases/Case.types";
import { initialCaseFilter } from "../cases/CaseService";
import { CaseSummarization } from "../cases/CaseSummarizationPage";
import CaseTableWrapper from "../cases/CaseTableWrapper";
import BarChart from "../charts/BarChart";
import DonutChart from "../charts/DonutChart";
import { YearlyCosts } from "../costs/Cost.types";
import noDataIcon from "../icons/No data.svg";
import serviceUnavailableIcon from "../icons/Service unavailable.svg";
import { Currency } from "../organizations/Organization.types";
import { ApplicationUser } from "../users/User.types";
import { CaseStatus } from "../worldMap/WorldMap.types";
import BarChartSkeleton from "./BarChartSkeleton";
import {
    FutureEstimatedRenewalCost,
    FutureEstimatedRenewalCostsView,
    RegistrationsPerClass,
    WidgetConfig,
    YearlyRenewalsAndApplications,
} from "./Dashboard.types";
import { DashboardWidgetGridItem } from "./DashboardGrid";
import { getUpcomingRenewals } from "./DashboardService";
import { buildFutureEstimatedRenewalCostsChart, futureEstimatedRenewalCostsFootnote } from "./FutureEstimatedRenewalCosts";
import FutureEstimatedRenewalCostsViewSelector from "./FutureEstimatedRenewalCostsViewSelector";

export const thousandSeparateNumber = (number: number): string => (number !== 0 ? number.toLocaleString("en") : "0");

export const findMax = (array: number[]) => array.reduce((prev, current) => (prev > current ? prev : current));

export const getDigitCount = (number: number) => number.toString().split(".")[0].length;

export const generateRenewalsAndApplicationsWidget = (
    widgetConfig: WidgetConfig<YearlyRenewalsAndApplications[]>,
    caseType: CaseType
): DashboardWidgetGridItem => {
    const indexBy: keyof YearlyRenewalsAndApplications = "year";
    let colors = [];
    let chartKeys = [];
    let header = "Renewals";
    if (caseType !== CaseType.Watches) {
        chartKeys.push("applications");
        colors.push(barChartDarkGrey);
        header = "Renewals and new applications";
    }
    chartKeys.push("renewals");
    colors.push("#B8C7FF");
    const tickValues = Math.max(
        ...chartKeys
            .filter((k) => widgetConfig.data.some((d) => d[k as keyof YearlyRenewalsAndApplications]))
            .map((key) => Math.max(...widgetConfig.data.map((o) => o[key as keyof YearlyRenewalsAndApplications])))
    );
    const legend = chartKeys.length > 0 ? chartKeys.map((ck) => ck.charAt(0).toUpperCase() + ck.slice(1)) : [];
    const yDigits =
        widgetConfig.data.length > 0 ? getDigitCount(findMax(widgetConfig.data.map((c) => (c.renewals > c.applications ? c.renewals : c.applications)))) : 1;

    return {
        content:
            widgetConfig.data.length === 0 ? (
                generateNoDataDisplay()
            ) : (
                <BarChart
                    colors={(data) => {
                        if (data.id === "applications") return barChartDarkGrey;
                        if (data.id === "renewals") return "#B8C7FF";
                        return barChartDarkGrey;
                    }}
                    data={widgetConfig.data}
                    digitsForLeftMargin={yDigits}
                    // Uses the whole content area, so the legend sits under the x axis instead of after a gap whose
                    // size is whatever the card stretched to.
                    fill
                    indexBy={indexBy}
                    keys={chartKeys.filter((k) => widgetConfig.data.some((d) => d[k as keyof YearlyRenewalsAndApplications]))}
                    tickValues={tickValues < 6 ? tickValues : 6}
                    xAxisTitle="Years"
                    yAxisTitle={header}
                />
            ),
        loadingContent: <BarChartSkeleton />,
        header: header,
        isLoading: widgetConfig.isLoading,
        legend: {
            colors: colors,
            legends: widgetConfig.data.length === 0 ? [] : legend,
        },
        serviceUnavailable: widgetConfig.serviceUnavailable,
        accessDenied: widgetConfig.accessDenied,
    };
};

export const generateRegistrationsPerClassWidget = (widgetConfig: WidgetConfig<RegistrationsPerClass>): DashboardWidgetGridItem => ({
    content:
        widgetConfig.data.totalCaseCount === 0 ? (
            generateNoDataDisplay()
        ) : (
            <DonutChart
                isLoading={false}
                colors={colors}
                chartData={{
                    totalCount: widgetConfig.data.totalCaseCount,
                    chartItems: widgetConfig.data.countByTop12Classes.map((c) => ({
                        id: c.class,
                        value: c.count,
                    })),
                }}
            />
        ),
    loadingContent: (
        <div
            style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                alignSelf: "center",
                height: 260,
                width: 260,
            }}
        >
            <Skeleton
                style={{
                    height: "100%",
                    width: "100%",
                }}
                animation="pulse"
                variant="circular"
            />
        </div>
    ),
    header: "Registrations per class",
    isLoading: widgetConfig.isLoading,
    legend: {
        colors,
        legends: widgetConfig.data.countByTop12Classes.map((c) => c.class),
    },
    serviceUnavailable: widgetConfig.serviceUnavailable,
    accessDenied: widgetConfig.accessDenied,
});

export const generateCostWidget = (widgetConfig: WidgetConfig<YearlyCosts[]>, caseType: CaseType): DashboardWidgetGridItem => {
    const keys = widgetConfig.data.length > 0 ? Object.keys(widgetConfig.data[0]) : [];
    const indexBy: keyof YearlyCosts = "year";
    const chartKeys = keys.length > 0 ? keys.filter((k) => (k as keyof YearlyCosts) !== indexBy) : [];
    const yDigits = widgetConfig.data.length > 0 ? getDigitCount(findMax(widgetConfig.data.map((c) => c.total))) : 1;

    return {
        content:
            widgetConfig.data.length === 0 ? (
                generateNoDataDisplay()
            ) : (
                <BarChart
                    colors={barChartDarkGrey}
                    data={widgetConfig.data}
                    digitsForLeftMargin={yDigits}
                    // Uses the whole content area, so the legend sits under the x axis instead of after a gap whose
                    // size is whatever the card stretched to.
                    fill
                    indexBy={indexBy}
                    keys={chartKeys}
                    unit="kr."
                    xAxisTitle="Years"
                    yAxisTitle="Amount in DKK"
                />
            ),
        loadingContent: <BarChartSkeleton />,
        footerChildren: (
            <div style={{ color: "#707070", fontSize: "10px" }}>
                {!widgetConfig.isLoading && widgetConfig.data.length > 0
                    ? `*The costs are not inclusive of VAT and do not cover costs for authorities and liaisons. The
        costs correspond to the invoiced amount and may, consequently, include amounts not yet paid.`
                    : ``}
            </div>
        ),
        header: "Costs",
        isLoading: widgetConfig.isLoading,
        legend: {
            colors: [barChartDarkGrey],
            legends:
                !widgetConfig.isLoading && widgetConfig.data.length === 0
                    ? []
                    : [`Total ${caseType.toLocaleLowerCase()} costs ${caseType !== CaseType.Watches ? "  - renewals and new applications" : ""}`],
        },
        serviceUnavailable: widgetConfig.serviceUnavailable,
        accessDenied: widgetConfig.accessDenied,
    };
};

export const generateFutureEstimatedRenewalCostsWidget = (
    widgetConfig: WidgetConfig<FutureEstimatedRenewalCost[]>,
    currency: Currency,
    view: FutureEstimatedRenewalCostsView,
    onViewChange: (view: FutureEstimatedRenewalCostsView) => void
): DashboardWidgetGridItem => {
    const chartKeys: Array<keyof FutureEstimatedRenewalCost> = ["estimatedRenewalCosts"];
    const chart = buildFutureEstimatedRenewalCostsChart(widgetConfig.data, view);
    const yDigits = chart.amounts.length > 0 ? getDigitCount(findMax(chart.amounts)) : 1;
    const currencyIsEuro = currency === Currency.EUR;
    const hasData = !widgetConfig.isLoading && !widgetConfig.serviceUnavailable && !widgetConfig.accessDenied && widgetConfig.data.length > 0;

    return {
        content:
            widgetConfig.data.length === 0 ? (
                generateNoDataDisplay()
            ) : (
                <BarChart
                    colors={barChartDarkGrey}
                    data={chart.data}
                    digitsForLeftMargin={yDigits}
                    // Uses the whole content area, so the legend sits under the x axis instead of after a gap whose
                    // size is whatever the card stretched to.
                    fill
                    indexBy={chart.showMonths ? "month" : "year"}
                    keys={chartKeys}
                    // A period without any costs only needs the 0 line, and it has to be given a maximum to keep
                    // that line at the bottom next to the x axis labels.
                    maxValue={chart.hasNoAmounts ? 1 : undefined}
                    tickValues={chart.hasNoAmounts ? [0] : undefined}
                    padding={chart.showMonths ? 0.5 : undefined}
                    seriesLabels={{ estimatedRenewalCosts: "Estimated renewal costs" }}
                    unit={currencyIsEuro ? "€" : "kr."}
                    xAxisTitle={chart.showMonths ? `Months, ${chart.selectedYear}` : "Years"}
                    yAxisTitle={currencyIsEuro ? "Amount in EUR" : "Amount in DKK"}
                />
            ),
        loadingContent: <BarChartSkeleton />,
        footerChildren: (
            <div style={{ color: "#707070", fontSize: "10px" }}>
                {!widgetConfig.isLoading && widgetConfig.data.length > 0
                    ? futureEstimatedRenewalCostsFootnote(chart.showMonths, chart.selectedYear, new Date().getFullYear())
                    : ``}
            </div>
        ),
        header: "Future estimated renewal costs",
        headerActions: hasData ? (
            <FutureEstimatedRenewalCostsViewSelector
                availableYears={chart.availableYears}
                onChange={onViewChange}
                view={{ mode: view.mode, selectedYear: chart.selectedYear }}
            />
        ) : undefined,
        isLoading: widgetConfig.isLoading,
        legend: {
            colors: [barChartDarkGrey],
            legends: !widgetConfig.isLoading && widgetConfig.data.length === 0 ? [] : ["Estimated renewal costs"],
        },
        serviceUnavailable: widgetConfig.serviceUnavailable,
        accessDenied: widgetConfig.accessDenied,
    };
};

export const generateUpcomingRenewalsWidget = (
    caseStatus: CaseStatus,
    caseType: CaseType,
    renewalRegistrationActive: boolean,
    editSetRenewalStatus: boolean,
    verifiedUser: ApplicationUser | null
): DashboardWidgetGridItem => ({
    content: (
        <CaseTableWrapper
            showRenewalPrices={false}
            editRenewalPrice={false}
            editSetRenewalStatus={editSetRenewalStatus}
            caseFilter={initialCaseFilter(caseStatus, CaseSummarization.Dashboard)}
            caseSummarization={CaseSummarization.Dashboard}
            downloadExcel={() => {}}
            isLoadingExcel={false}
            onCaseFilterChanged={getUpcomingRenewals}
            selectedCaseType={caseType}
            renewalRegistrationActive={renewalRegistrationActive}
            verifiedUser={verifiedUser}
        />
    ),
    loadingContent: <div />,
    isLoading: false,
    header: `${caseType === CaseType.Copyright ? `Expiries` : `Upcoming Renewals`}`,
    serviceUnavailable: false,
    accessDenied: false,
});

const barChartDarkGrey = "#6B6258";

const colors = ["#000000", "#B8C7FF", "#EDE7DF", "#97A8E8", "#A89E90", "#B39BC7", "#D9D3CC", "#8F97D6", "#9C9184", "#A488B5", "#6B6258", "#D6DEFF"];

const generateWidgetDisplay = (marginAuto: "marginAuto" | undefined = undefined, height: number | string | undefined = undefined, icon: any) => (
    <div
        style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: height || "100%",
            margin: marginAuto ? "auto" : undefined,
        }}
    >
        <img src={icon} alt="" />
    </div>
);

export const generateNoDataDisplay = (marginAuto: "marginAuto" | undefined = undefined, height: number | string | undefined = undefined) =>
    generateWidgetDisplay(marginAuto, height, noDataIcon);

export const generateServiceUnavailableDisplay = (marginAuto: "marginAuto" | undefined = undefined, height: number | string | undefined = undefined) =>
    generateWidgetDisplay(marginAuto, height, serviceUnavailableIcon);
